// opervex-frontend/src/pages/System/AccessProfile.js

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Form, InputGroup, Card, Button } from "react-bootstrap";
import { FaSearch, FaPlus, FaSave, FaPencilAlt } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import Tree from "@widgetjs/tree";

import { useAuth } from "../../contexts/AuthContext";
import { SearchHelpModal } from "../../utils/SearchHelp";
import useOpervex from "../../hooks/useOpervex";

const rolePageRequire = "USER_PROFILE";

const dataDefault = {
  header: { id: "", username: "", fullName: "" },
  profiles: [],
};

const actionsDefault = {
  create: true,
  change: true,
  delete: true,
  save: true,
};

var treeRef = null;

const AccessProfile = () => {
  const { roles, loadingGlobal } = useAuth();
  const { id } = useParams();
  const OpervexAPI = useOpervex();

  const [disabledField, setDisabledField] = useState(true);
  const [actions, setActions] = useState(actionsDefault);
  const [treeData, setTreeData] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [data, setData] = useState(dataDefault);

  const buildNodes = (profileList, parent, nodekey) => {
    return profileList
      .filter((x) => x.parent === parent)
      .map((profile) => {
        // Create the base profile node
        const node = {
          id: `${nodekey}_${profile.id}`,
          text: profile.description,
          attributes: {
            actionTop: { id: 3, key: "Read" },
            parentId: profile.id,
          },
          children: [],
        };

        // Add action nodes as children
        profile.actions
          .filter((x) => x.key !== "Read")
          .forEach((action) => {
            node.children.push({
              id: `${nodekey}_${profile.id}_${action.key}`,
              text: action.name,
              attributes: {
                action: action,
                parentId: profile.id,
              },
              leaf: true, // Mark as leaf node (no children)
            });
          });

        profile.actions
          .filter((x) => x.key === "Read")
          .forEach((action) => {
            if (node.children.length !== 0)
              node.children.push({
                id: `${nodekey}_${profile.id}_${action.key}`,
                text: action.name,
                attributes: {
                  action: action,
                  parentId: profile.id,
                },
                leaf: true, // Mark as leaf node (no children)
              });
          });

        // Recursively add child profiles (will appear after actions)
        const childProfiles = buildNodes(profileList, profile.id, `${nodekey}_${profile.id}`);

        node.children.push(...childProfiles);

        if (!node.children.length && profile.actions.length === 1 && profile.actions[0].key === "Read") {
          node.attributes = {
            action: profile.actions[0],
            parentId: profile.id,
          };
        }

        return node;
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        commandAction({ ...actionsDefault, create: false });
        const profileList = await OpervexAPI.System.Profile.findAll();
        const newTreeData = buildNodes(profileList, null, "node");
        setTreeData(newTreeData);
        setProfiles(profileList);
      } catch (error) {}
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionsDefault]);

  useEffect(() => {
    if (!loadingGlobal && treeData.length > 0) {
      const newTreeRef = new Tree("#treeView", {
        data: treeData,
      });

      treeRef = newTreeRef;

      updateDisabledField(disabledField);

      if (id) searchAPI(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingGlobal, treeData]);

  if (loadingGlobal) return <div>Carregando...</div>;

  const commandAction = (actions) => {
    const hasPermission = roles.find((role) => role.command === rolePageRequire);

    if (!hasPermission) {
      setActions(actionsDefault);
      return;
    }

    actions.create = true;

    if (!actions.change) actions.change = !hasPermission.actions.some((action) => action.key === "Change");

    setActions({ ...actions });
  };

  const searchAPI = async (id) => {
    if (!id) return;

    updateDisabledField(false);

    try {
      const [resultHeader, resultProfiles] = await Promise.all([
        OpervexAPI.System.User.findId(id),
        OpervexAPI.System.UserProfile.findUserId(id),
      ]);

      updateData({ header: resultHeader, profiles: resultProfiles });

      commandAction({ ...actionsDefault, create: false, change: false });

      updateDisabledField(true);
    } catch (error) {
      alert(`Erro na busca: ${error.message}`);
    }
  };

  const updateData = async (newData) => {
    try {
      // Early return if invalid data
      if (!treeRef || !newData?.profiles?.length) {
        setData(newData || {});
        treeRef.values = [];
        return;
      }

      setData(newData);
      const validNodeIds = [];

      // Cache profiles by id for faster lookup
      const profileMap = new Map();
      newData.profiles.forEach((profile) => {
        profileMap.set(profile.id, profile);
      });

      // Check each node in the tree
      for (const [id, node] of Object.entries(treeRef.nodesById)) {
        const { attributes } = node;

        // Skip if node doesn't have required attributes
        if (!attributes?.parentId || !attributes?.action?.key) continue;

        const profile = profileMap.get(attributes.parentId);
        if (!profile) continue;

        // Check if action still exists in the profile
        const actionExists = profile.actions.some((action) => action.key === attributes.action.key);

        if (actionExists) {
          validNodeIds.push(id);
        }
      }

      treeRef.values = validNodeIds;
    } catch (error) {
      if (treeRef) treeRef.values = [];
    }
  };

  const handleButtonCreate = () => {
    setData(dataDefault);
    commandAction({ ...actionsDefault, save: false });
    updateDisabledField(false);
  };

  const handleButtonCancel = () => {
    if (data.header.id === "") {
      setData(dataDefault);
      updateDisabledField(true);
      commandAction({ ...actionsDefault, create: false });
    } else {
      searchAPI(data.header.id);
    }
  };

  const handleButtonSave = async () => {
    var body = [];

    treeRef.selectedNodes.forEach((selectedNode) => {
      const node = treeRef.nodesById[selectedNode.id];

      const profile = profiles.find((x) => x.id === node.attributes.parentId);

      const action = profile?.actions.find((x) => x.key === (node.attributes.action?.key || node.attributes.actionTop.key));

      if (body.findIndex((x) => x.id === profile.id) === -1) body.push({ ...profile, actions: [] });

      const item = body.find((x) => x.id === profile.id);
      if (item && !item.actions.find((a) => a === action)) item.actions.push(action);
    });

    try {
      const result = await OpervexAPI.System.UserProfile.update(data.header.id, body);

      if (result.error) {
        alert(result.error);
        return;
      }

      await searchAPI(data.header.id);
      alert("Acessos atualizados com sucesso!");
    } catch (error) {
      alert("Ocorreu um erro ao salvar: " + error.message);
    }
  };

  const handleButtonSearch = async () => {
    const item = await SearchHelpModal({
      urlData: "/System/Users/SearchHelp",
      schema: await OpervexAPI.System.User.schema(),
    });

    if (item && item.id) await searchAPI(item.id);
  };

  const handleButtonChange = () => {
    updateDisabledField(false);
    commandAction({ ...actionsDefault, save: false });
  };

  const updateDisabledField = async (action) => {
    setDisabledField(action);

    if (treeRef) {
      var ids = [];

      for (const id in treeRef.nodesById) ids.push(id);

      if (action) treeRef.disables = ids;
      else treeRef.disables = [];
    }
  };

  return (
    <Container className="mt-2">
      <Form>
        <Card className="mb-2">
          <Card.Header>
            <Row className="d-flex justify-content-between">
              <Col md={8}>
                <h5>Cabeçalho</h5>
              </Col>
              <Col md={4} className="text-end">
                <Button variant="success" size="sm" className="me-2" onClick={handleButtonCreate} hidden={actions.create}>
                  <FaPlus /> Novo
                </Button>
                <Button variant="primary" size="sm" className="me-2" onClick={handleButtonChange} hidden={actions.change}>
                  <FaPencilAlt /> Alterar
                </Button>
                <Button variant="danger" size="sm" className="me-2" onClick={handleButtonCancel} hidden={actions.save}>
                  <MdOutlineCancel /> Cancelar
                </Button>
                <Button variant="primary" size="sm" className="me-2" onClick={handleButtonSave} hidden={actions.save}>
                  <FaSave /> Salvar
                </Button>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            <Row className="mb-2">
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Código</Form.Label>
                  <InputGroup>
                    <Form.Control type="text" size="sm" value={data?.header?.id} name="id" disabled />
                    <InputGroup.Text size="sm" as="button" type="button" onClick={handleButtonSearch}>
                      <FaSearch />
                    </InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Usuário</Form.Label>
                  <Form.Control type="text" size="sm" name="username" value={data?.header?.username} disabled />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Nome</Form.Label>
                  <Form.Control type="text" size="sm" name="fullName" value={data?.header?.fullName} disabled />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <Card className="mb-2">
          <Card.Header>
            <h5>Acessos</h5>
          </Card.Header>
          <Card.Body>
            <div disabled={disabledField} id="treeView"></div>
          </Card.Body>
        </Card>
      </Form>
    </Container>
  );
};

export default AccessProfile;
